import React, { useState } from 'react';
import {
  Typography,
  Modal,
  Box,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  Stepper,
  StepLabel,
  StepContent,
  Step,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useApiSend } from '../../utils/httpClientTs';
import { completeTraining } from '../../endpoints/training';
import {
  TrainingQuestion,
  QuizQuestionProps,
  QuizModalProps,
} from '../../types/training';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 600,
  maxWidth: '90vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  border: '2px solid #0072CE',
  borderRadius: '16px',
  boxShadow: 24,
  p: 0,
  overflow: 'hidden',
};

interface AnswerOption {
  option: string;
  answer: string;
}

const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question,
  handleBack,
  handleNext,
  activeStep,
  steps,
}) => {
  const [answer, setAnswer] = useState<string | null>(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState(false);

  const answerOptions: AnswerOption[] = [];
  Object.keys(question).forEach((key) => {
    if (key.includes('selection') && question[key as keyof TrainingQuestion]) {
      const temp = key.split('_')[1];
      answerOptions.push({
        option: temp,
        answer: question[key as keyof TrainingQuestion] as string,
      });
    }
  });

  const checkAnswer = () => {
    setAnswerSubmitted(true);
    if (answer === question.answer) {
      setAnswerCorrect(true);
      return;
    }
    setAnswerCorrect(false);
  };

  const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnswer(event.target.value);
  };

  const isLastQuestion = activeStep === steps.length - 1;

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Question {activeStep + 1} of {steps.length}
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}
          >
            {question.question}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={answer}
              onChange={handleAnswerChange}
              sx={{ gap: 1 }}
            >
              {answerOptions
                .sort((a, b) => a.option.localeCompare(b.option))
                .map((option) => (
                  <FormControlLabel
                    key={option.option}
                    value={option.option}
                    control={<Radio color="primary" />}
                    label={
                      <Typography variant="body1" sx={{ ml: 1 }}>
                        {option.answer}
                      </Typography>
                    }
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&.Mui-checked': {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#0072CE',
                      },
                    }}
                  />
                ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {answerSubmitted ? (
        <Alert
          severity={answerCorrect ? 'success' : 'error'}
          sx={{ mb: 3 }}
          icon={answerCorrect ? <CheckCircleIcon /> : <CancelIcon />}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                {answerCorrect ? 'Correct!' : 'Incorrect!'}
              </Typography>
              {!answerCorrect && (
                <Typography variant="body2">
                  The correct answer is:{' '}
                  {
                    answerOptions.find((opt) => opt.option === question.answer)
                      ?.answer
                  }
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              color={answerCorrect ? 'success' : 'error'}
              endIcon={
                isLastQuestion ? <CheckCircleIcon /> : <ArrowForwardIcon />
              }
              onClick={() => handleNext(answerCorrect)}
              size="large"
            >
              {isLastQuestion ? 'Finish Quiz' : 'Next Question'}
            </Button>
          </Box>
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            disabled={answer === null}
            size="large"
            color="primary"
            onClick={checkAnswer}
            endIcon={<CheckCircleIcon />}
          >
            Check Answer
          </Button>
        </Box>
      )}
    </Box>
  );
};

const QuizModal: React.FC<QuizModalProps> = ({
  quizOpen,
  setOpen,
  quizQuestions,
  trainingId,
  refetchParent,
}) => {
  const steps = quizQuestions.map((question, index) => ({
    label: `Question ${index + 1}`,
    question: question,
  }));

  const [activeStep, setActiveStep] = useState(0);
  const [questionsIncorrect, setQuestionsIncorrect] = useState(0);
  const [completedQuestions, setCompletedQuestions] = useState<boolean[]>([]);

  const { mutate, isPending } = useApiSend(
    completeTraining,
    (data) => {
      refetchParent();
      handleReset();
      setOpen(false);
    },
    (error) => {
      console.error('Error completing training:', error);
    }
  );

  const handleNext = (correct: boolean) => {
    const newCompleted = [...completedQuestions];
    newCompleted[activeStep] = correct;
    setCompletedQuestions(newCompleted);

    if (!correct) {
      setQuestionsIncorrect(questionsIncorrect + 1);
    }

    if (activeStep === steps.length - 1) {
      handleComplete();
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setQuestionsIncorrect(0);
    setCompletedQuestions([]);
  };

  const handleComplete = () => {
    if (questionsIncorrect === 0) {
      mutate({
        id: trainingId,
      });
    } else {
      setOpen(false);
      handleReset();
    }
  };

  const handleClose = () => {
    setOpen(false);
    handleReset();
  };

  const progress = ((activeStep + 1) / steps.length) * 100;
  const correctAnswers = completedQuestions.filter(Boolean).length;
  const totalAnswered = completedQuestions.filter(
    (answer) => answer !== undefined
  ).length;

  return (
    <Modal
      open={quizOpen}
      onClose={handleClose}
      aria-labelledby="quiz-modal-title"
      aria-describedby="quiz-modal-description"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            backgroundColor: '#0072CE',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QuizIcon sx={{ mr: 2, fontSize: 32 }} />
            <Box>
              <Typography variant="h5" component="h2" gutterBottom>
                Training Quiz
              </Typography>
              <Typography variant="body2">
                {steps.length} questions • {correctAnswers}/{totalAnswered}{' '}
                correct
              </Typography>
            </Box>
          </Box>
          <Button
            onClick={handleClose}
            sx={{ color: 'white' }}
            startIcon={<CancelIcon />}
          >
            Close
          </Button>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              Progress: {activeStep + 1} of {steps.length}
            </Typography>
            <Chip
              label={`${Math.round(progress)}%`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6">{step.label}</Typography>
                    {completedQuestions[index] !== undefined && (
                      <Chip
                        icon={
                          completedQuestions[index] ? (
                            <CheckCircleIcon />
                          ) : (
                            <CancelIcon />
                          )
                        }
                        label={
                          completedQuestions[index] ? 'Correct' : 'Incorrect'
                        }
                        color={completedQuestions[index] ? 'success' : 'error'}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Box>
                </StepLabel>
                <StepContent>
                  <QuizQuestion
                    key={step.question.id || index}
                    question={step.question}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    steps={steps}
                    activeStep={activeStep}
                  />
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {questionsIncorrect > 0 &&
              `Incorrect answers: ${questionsIncorrect}`}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            {activeStep === steps.length - 1 && (
              <Button
                variant="contained"
                color="success"
                onClick={handleComplete}
                disabled={isPending}
                startIcon={<CheckCircleIcon />}
              >
                Complete Training
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default QuizModal;
